import { z } from 'zod';
import { Id, IsoDate, PageQuerySchema, UserRefSchema } from './common';
/* ============================================================================
   Notifications (PLAN §8A): unseen → seen (visible ≥60% for 1s) → read (clicked)
   Badge counts UNSEEN. Recipients are computed with RBAC (admin-only types).
   ========================================================================== */
export const NotificationTypes = [
    'mission.assigned',
    'mission.mentioned',
    'mission.commented',
    'mission.status_changed',
    'mission.due_soon',
    'mission.overdue',
    'mission.completed',
    'invite.accepted',
    'member.joined',
    'plan.expiring',
    'plan.changed',
    'domain.live',
    'export.ready',
    'broadcast',
];
/** Types only Owner/Admin receive. */
export const ADMIN_ONLY_NOTIFICATION_TYPES = [
    'member.joined',
    'plan.expiring',
    'plan.changed',
    'domain.live',
    'export.ready',
];
/** Types an Admin can force on (users can't turn off in-app). */
export const FORCEABLE_NOTIFICATION_TYPES = ['mission.assigned', 'mission.mentioned'];
export const NotificationSchema = z.object({
    id: Id,
    type: z.enum(NotificationTypes),
    actor: UserRefSchema.nullable(),
    /** rendered text parts; UI applies the mission/neutral terms dictionary */
    title: z.string(),
    body: z.string().nullable(),
    entity: z.object({
        type: z.enum(['mission', 'project', 'tenant', 'member', 'export', 'broadcast']),
        id: Id.nullable(),
        key: z.string().nullable(),
    }),
    project: z.object({ id: Id, name: z.string(), color: z.string() }).nullable(),
    /** deep link inside the app, e.g. /p/{projectId}/m/{missionId} */
    href: z.string().nullable(),
    seenAt: IsoDate.nullable(),
    readAt: IsoDate.nullable(),
    createdAt: IsoDate,
});
export const NotificationListQuery = PageQuerySchema.extend({
    tab: z.enum(['all', 'unread', 'mentions', 'assigned']).default('all'),
});
export const NotificationCountsSchema = z.object({
    unseen: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
    mentions: z.number().int().nonnegative(),
    assigned: z.number().int().nonnegative(),
});
export const NotificationIdsSchema = z.object({ ids: z.array(Id).min(1).max(200) });
export const ReadAllResultSchema = z.object({ updated: z.number().int(), undoToken: z.string() });
export const PreferenceRowSchema = z.object({
    type: z.enum(NotificationTypes),
    inApp: z.boolean(),
    email: z.boolean(),
    push: z.boolean(),
    /** forced by an Admin: in-app can't be turned off */
    locked: z.boolean(),
});
export const NotificationPreferencesSchema = z.object({
    rows: z.array(PreferenceRowSchema),
    quietHours: z.object({
        enabled: z.boolean(),
        from: z.string().regex(/^\d{2}:\d{2}$/),
        to: z.string().regex(/^\d{2}:\d{2}$/),
    }),
    digest: z.object({ enabled: z.boolean(), time: z.string().regex(/^\d{2}:\d{2}$/) }),
    muteTypes: z.array(z.enum(NotificationTypes)),
});
export const NotificationPreferencesUpdateSchema = NotificationPreferencesSchema.partial();
export const MuteMissionSchema = z.object({ missionId: Id, muted: z.boolean() });
/* ---------- Web push (VAPID) ---------- */
export const PushConfigSchema = z.object({ vapidPublicKey: z.string(), enabledForTenant: z.boolean() });
export const PushSubscribeSchema = z.object({
    endpoint: z.string().url(),
    keys: z.object({ p256dh: z.string(), auth: z.string() }),
    deviceLabel: z.string().max(80).optional(),
});
export const PushDeviceSchema = z.object({
    id: Id,
    deviceLabel: z.string(),
    createdAt: IsoDate,
    lastUsedAt: IsoDate.nullable(),
    current: z.boolean(),
});
//# sourceMappingURL=notifications.js.map